import { FeatureFlags } from '@fabric-ui/components';
import { getKeyByValue } from '../../../components/SelectWidgetType/SelectWidgetType';
import { DimensionTypes, FieldToCheck, LimitState, widgetLimitConfigs } from '../../../constants';
import { AllTypeWidgets, Field, TableSubType } from '../../../types';
import { FieldType } from '../../../types/types';

const METRICS_GROUP_NAME = getKeyByValue(FieldType, FieldType.Measurements);
const getDimsDisabledText = (limit: number) => `You can add up to ${limit} dimensions`;
const getMetricsDisabledText = (limit: number) => `You can add up to ${limit} measurements`;
const getDisabledObj = (disabledTooltip = '') => ({ disabled: true, disabledTooltip });

const isDimSelected = (dims: Field[], field: FieldToCheck) => !!dims.find(({ name }) => name === field.value);
const isMetricSelected = (metrics: Field[], field: FieldToCheck) => !!metrics.find(({ name }) => name === field.value);

const isDim = (field: FieldToCheck) => field.group !== METRICS_GROUP_NAME;
const isMetric = (field: FieldToCheck) => field.group === METRICS_GROUP_NAME;

export type SelectionLimitState = {
  metrics: LimitState;
  dims: LimitState;
};

export const shouldDisableField = ({
  newWidget,
  field,
  fieldType,
  featureFlags,
}: {
  newWidget: AllTypeWidgets;
  field: FieldToCheck;
  fieldType: DimensionTypes;
  featureFlags: Record<string, boolean>;
}) => {
  const { type } = newWidget;
  const { dims, metrics } = newWidget.requests[0].select;

  if (type === TableSubType.PivotTable) {
    const isRowDimSelected = !!dims.find(({ type: t }) => t === DimensionTypes.Row);
    const isNextFieldColDim = isRowDimSelected || fieldType === DimensionTypes.Col;

    if (field.repeated && isNextFieldColDim && !isDimSelected(dims, field)) {
      return getDisabledObj('Repeated fields cannot be selected as column dimensions');
    }
  }

  if (!featureFlags[FeatureFlags.CDOverSelection]) {
    return { disabled: false, disabledTooltip: '' };
  }

  const { category } = newWidget;
  const config = widgetLimitConfigs[type] || widgetLimitConfigs[category];

  if (config) {
    if (isMetric(field) && !isMetricSelected(metrics, field)) {
      const limit = config.metrics({ selectedDims: dims });
      if (metrics.length >= limit) {
        return getDisabledObj(getMetricsDisabledText(limit));
      }
    }

    if (isDim(field) && !isDimSelected(dims, field)) {
      if (type === TableSubType.PivotTable) {
        const currentDimType = fieldType;
        const limit = 1;

        const columnDims = dims.filter(d => d.type === DimensionTypes.Col);
        const rowDims = dims.filter(d => d.type === DimensionTypes.Row);
        if (currentDimType === DimensionTypes.Row) {
          if (rowDims.length >= limit) {
            return getDisabledObj(`You can add up to ${limit} row dimension`);
          }
        }
        if (currentDimType === DimensionTypes.Col) {
          if (columnDims.length >= limit) {
            return getDisabledObj(`You can add up to ${limit} column dimension`);
          }
        }
        if (columnDims.length >= limit && rowDims.length >= limit) {
          return getDisabledObj(`You can add up to ${limit} column dimensions and ${limit} row dimensions`);
        }
      } else {
        const limit = config.dims({ selectedMetrics: metrics });
        if (dims.length >= limit) {
          return getDisabledObj(getDimsDisabledText(limit));
        }
        if (config.customDimensionValidation) {
          const error = config.customDimensionValidation({ selectedDims: dims, field });
          if (error) {
            return getDisabledObj(error);
          }
        }
      }
    }
  }

  return { disabled: false, disabledTooltip: '' };
};

export const hasReachedSelectedFieldsLimit = (widget: AllTypeWidgets, featureFlags: Record<string, boolean>): SelectionLimitState => {
  const { category, type } = widget;
  const { dims, metrics } = widget.requests[0].select;

  const config = widgetLimitConfigs[type] || widgetLimitConfigs[category];
  if (config && featureFlags[FeatureFlags.CDOverSelection]) {
    const metricLimit = config.metrics({ selectedDims: dims });

    const dimensionLimitState = config.getDimensionLimitState
      ? config.getDimensionLimitState({ selectedDims: dims })
      : {
          isReached: dims.length >= config.dims({ selectedMetrics: metrics }),
          message: getDimsDisabledText(config.dims({ selectedMetrics: metrics })),
        };

    return {
      metrics: {
        isReached: metrics.length >= metricLimit,
        message: getMetricsDisabledText(metricLimit),
      },
      dims: dimensionLimitState,
    };
  }

  return {
    metrics: { isReached: false, message: '' },
    dims: { isReached: false, message: '' },
  };
};
